// src/pages/DropshipToolsPage.jsx
import React, { useState } from 'react';
import {
  Container, Typography, Tabs, Tab,
  Box, TextField, Button, LinearProgress,
  Paper, FormHelperText, Stack
} from '@mui/material';
import FlashOnIcon from '@mui/icons-material/FlashOn';

export default function DropshipToolsPage() {
  const [mode, setMode]       = useState('description');
  const [input, setInput]     = useState('');
  const [tone, setTone]       = useState('');
  const [result, setResult]   = useState('');
  const [loading, setLoading] = useState(false);

  const modes = [
    { value:'description',  label:'Generate Description', helper: 'E-commerce title + 5 bullets + SEO description.' },
    { value:'ad_copy',       label:'Ad-Copy',             helper: '3 FB ad variants: headline, body & CTA.' },
    { value:'niche',         label:'Find Niches',         helper: '5 hot niches with margins & ideas.' },
    { value:'keywords',      label:'Keyword Ideas',       helper: '20 high-ROI Google keywords.' },
    { value:'margin',        label:'Margin Calculator',   helper: 'Enter “cost:XX, price:YY”.' },
    { value:'shipping',      label:'Shipping Estimator',  helper: 'Enter “weight:KG,from:US,to:UK”.' },
    { value:'source_detect', label:'Detect Source',       helper: 'Paste full product-page URL.' },
  ];

  const currentHelper = modes.find(m => m.value === mode)?.helper;

  const handleRun = async () => {
    setLoading(true);
    setResult('');
    try {
      const res = await fetch('https://lemda.co.uk/web_api/ai_tool.php', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ mode, input, tone })
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setResult(json.result.trim());
    } catch(err){
      setResult(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  return (
    <Container sx={{ py:6, mt:4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb:2 }}>
        <FlashOnIcon fontSize="large" color="info" />
        <Typography variant="h4">Lemda Intelligence Toolkit</Typography>
      </Stack>

      <Typography variant="subtitle1" color="text.secondary" sx={{ mb:4, maxWidth:600 }}>
        Supercharge your dropshipping workflow—generate descriptions, ad-copy, niche insights, calculate margins & shipping, or detect if a product page is a third-party listing. Powered end-to-end by Lemda Intelligence.
      </Typography>

      <Paper elevation={2} sx={{ mb:3 }}>
        <Tabs
          value={mode}
          onChange={(_,v)=> setMode(v)}
          indicatorColor="secondary"
          textColor="secondary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {modes.map(m=>(
            <Tab key={m.value} label={m.label} value={m.value}/>
          ))}
        </Tabs>
      </Paper>

      <Box component={Paper} elevation={1} sx={{ p:3, mb:4 }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          {currentHelper}
        </Typography>

        <TextField
          label={
            mode === 'niche'
              ? 'Your Market (e.g. US fashion)'
              : mode === 'source_detect'
                ? 'Product Page URL'
                : 'Product Name / Parameters'
          }
          placeholder={
            mode === 'margin'    ? 'cost:10, price:25'
          : mode === 'shipping'  ? 'weight:0.5, from:US, to:UK'
          : ''
          }
          fullWidth
          value={input}
          onChange={e=> setInput(e.target.value)}
          sx={{ mb: 2 }}
        />

        {mode === 'description' && (
          <TextField
            label="Tone (optional)"
            fullWidth
            value={tone}
            onChange={e=> setTone(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <Box sx={{ textAlign:'right' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleRun}
            disabled={!input.trim() || loading}
          >
            {loading ? 'Analyzing…' : 'Run'}
          </Button>
        </Box>

        {loading && <LinearProgress sx={{ mt:2 }} />}
      </Box>

      {result && (
        <Paper elevation={1} sx={{ p:3 }}>
          <Typography variant="h6" gutterBottom>
            Result
          </Typography>
          <Typography component="pre" sx={{ whiteSpace:'pre-wrap' }}>
            {result}
          </Typography>
        </Paper>
      )}
    </Container>
  );
}
